import { SettingsIcon } from '@/assets/icons'
import PageLoader from '@components/PageLoader'
import { Hero, TripOverview, TripOverviewSection } from '@components/TripOverview'
import { buildTripOverviewData, sectionPath } from '@components/TripOverview/core'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import {
	convertDateRangeToPayload,
	useAccommodations,
	useEvents,
	useExpenses,
	useFiles,
	useFlights,
	useIdeas,
	useMe,
	usePayUps,
	useTravelSegments,
	useTrip,
	useTripMembers,
	useUpdateTripDates,
} from '@hooks'
import { isConflict } from '@lib/apiError'
import { isInlineImageMime } from '@lib/mime'
import { TripHelper } from '@lib/TripHelper'
import { sortGroupsByDeparture } from '@pages/Flights/flightHelpers'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import EditTripDatesModal from '../../Itinerary/EditTripDatesModal/EditTripDatesModal'
import EditTripModal from '../EditTripModal'
import { ErrorState, OverviewContainer, OverviewWrapper, TripSettingsButton } from './TripDetailPage.styles'

const TripDetailPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()
	const { data: eventsData, isLoading: eventsLoading } = useEvents(tripId)
	// Segments enhance the itinerary preview when they land — they never gate it.
	const { data: travelSegmentsData } = useTravelSegments(tripId)
	const { data: ideasData, isLoading: ideasLoading } = useIdeas(tripId)
	const { data: membersData } = useTripMembers(tripId)
	const { data: accommodationsData, isLoading: accommodationsLoading } = useAccommodations(tripId)
	const { data: flightsData, isLoading: flightsLoading } = useFlights(tripId)
	const { data: filesData, isLoading: filesLoading } = useFiles(tripId)
	const { data: expensesData, isLoading: expensesLoading } = useExpenses(tripId)
	const { data: payUpsData, isLoading: payUpsLoading } = usePayUps(tripId)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false)
	const updateTripDates = useUpdateTripDates(tripId || '')

	// The trip overview is the trail's ROOT (every other trip page starts its
	// breadcrumbs at the trip name) — it renders no trail of its own.
	useBreadcrumbs([])

	if (tripLoading) {
		return (
			<OverviewWrapper>
				<PageLoader label="Loading trip details…" />
			</OverviewWrapper>
		)
	}

	if (!trip) {
		return (
			<OverviewWrapper>
				<OverviewContainer>
					<ErrorState>Trip not found</ErrorState>
				</OverviewContainer>
			</OverviewWrapper>
		)
	}

	// Unwrap the hook responses into the pure normalizer's inputs. Joins that need
	// two queries (accommodation photo ← files) happen here, not in the core.
	const files = filesData?.files ?? []
	const filesById = new Map(files.map(file => [file.id, file]))

	// The stay's first attachment, matching the public overview. A non-image
	// fails to load and Thumbnail falls back to its icon chip.
	const stayPhotoUrl = (fileIds: readonly string[]): string | null =>
		(fileIds[0] && filesById.get(fileIds[0])?.url) || null

	const firstLegs = sortGroupsByDeparture(flightsData?.flightGroups ?? [])
		.map(group => group.flights[0])
		.filter(leg => leg != null)

	const overviewData = buildTripOverviewData({
		trip: { name: trip.name, startDate: trip.startDate ?? null, dayCount: trip.dayCount },
		members: membersData?.members,
		events: eventsData?.events,
		travelSegments: travelSegmentsData?.segments,
		ideas: ideasData?.ideas?.map(idea => ({
			id: idea.id,
			name: idea.name,
			thumbnailUrl: idea.links.find(link => link.thumbnail)?.thumbnail ?? null,
		})),
		accommodations: accommodationsData?.accommodations?.map(stay => ({
			id: stay.id,
			name: stay.name,
			city: stay.location?.locality || stay.location?.secondaryText || null,
			photoUrl: stayPhotoUrl(stay.fileIds),
			checkInDate: stay.checkInDate,
			checkOutDate: stay.checkOutDate,
		})),
		flights: firstLegs.map(leg => ({
			id: leg.id,
			airlineIata: leg.airlineIata,
			flightNumber: leg.flightNumber,
			originCode: leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? null,
			destinationCode: leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? null,
			departureTime: leg.departureTime,
			departureTimeStr: leg.departureTimeStr,
			durationMinutes: leg.durationMinutes,
		})),
		files: files.map(file => ({
			id: file.id,
			fileName: file.fileName,
			url: file.url,
			thumbnailUrl: isInlineImageMime(file.mimeType) ? file.url : null,
			mimeType: file.mimeType,
		})),
		money: {
			defaultCurrencyCode: trip.defaultCurrencyCode,
			expenses: expensesData?.expenses,
			payUps: payUpsData?.payUps,
		},
	})

	// Per-tile skeletons while each tile's own queries load — no page spinner.
	const tileLoading = {
		itinerary: eventsLoading,
		ideas: ideasLoading,
		accommodations: accommodationsLoading,
		flights: flightsLoading,
		files: filesLoading,
		money: expensesLoading || payUpsLoading,
	}

	const tripStartDate = trip.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = (() => {
		if (!trip.startDate || !trip.dayCount) return ''
		const endDate = new TripHelper(trip).getEndDate()
		return endDate ? endDate.toISOString().split('T')[0] : ''
	})()

	const handleNavigate = (section: TripOverviewSection) => {
		navigate(sectionPath(tripId ?? '', section))
	}

	const handleSaveTripDates = (startDate: string, endDate: string) => {
		if (!tripId) return
		const payload = convertDateRangeToPayload({ startDate, endDate })
		updateTripDates.mutate(payload, {
			onSuccess: () => {
				toast.success('Trip dates updated successfully!')
				setIsEditModalOpen(false)
			},
			onError: (error) => {
				if (isConflict(error)) {
					toast.error(
						'Cannot shorten trip dates. You have events scheduled on days that would be removed. Please delete or move those events first.',
						{ duration: 10000 }
					)
				} else {
					toast.error('Failed to update trip dates. Please try again.')
				}
			},
		})
	}

	return (
		<OverviewWrapper>
			<OverviewContainer>
				<Hero
					hero={overviewData.hero}
					onAvatarsClick={() => navigate(`/trips/${tripId}/members`)}
					action={
						<TripSettingsButton onClick={() => setIsEditTripModalOpen(true)} aria-label="Trip settings">
							<SettingsIcon sx={{ fontSize: 18 }} />
							<span>Trip Settings</span>
						</TripSettingsButton>
					}
				/>

				<TripOverview
					data={overviewData}
					mode="member"
					loading={tileLoading}
					onNavigate={handleNavigate}
					onSetTripDates={() => setIsEditModalOpen(true)}
				/>
			</OverviewContainer>

			<EditTripDatesModal
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={handleSaveTripDates}
				initialStartDate={tripStartDate}
				initialEndDate={tripEndDate}
				isUpdating={updateTripDates.isPending}
			/>

			<EditTripModal
				isOpen={isEditTripModalOpen}
				onClose={() => setIsEditTripModalOpen(false)}
				tripId={tripId || ''}
				tripCode={trip.code}
				initialName={trip.name}
				initialCurrencyCode={trip.defaultCurrencyCode}
				isOwner={me ? me.id === trip.ownerUserId : undefined}
			/>
		</OverviewWrapper>
	)
}

export default TripDetailPage
